import {Component, OnDestroy, OnInit} from '@angular/core';
import {EQOShadow} from '@quickorder/angular-web-lib';
import {IAppState} from '@app/core/store/state/app.state';
import {Store} from '@ngrx/store';
import {Observable, Subscription} from 'rxjs';
import {Moment} from 'moment';
import {take} from 'rxjs/operators';
import {DayNote} from '@app/core/models/day-note.model';
import {selectPrinting, selectSelectedDates, selectShifts} from '@app/core/store/selectors/schedule.selectors';
import {selectDayNotes} from '@app/core/store/selectors/daynotes.selectors';
import {SetSelectedDate, SetSelectedDayNote} from '@app/core/store/actions/daynotes.actions';
import {IQODropDownAction} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-drop-down-action.interface';
import {SetSortingType} from '@app/core/store/actions/schedule.actions';
import {User} from '@app/core/models/user.model';
import {selectUser} from '@app/core/store/selectors/session.selectors';
import {getCurrentState} from '@app/core/helpers';
import {InternationalizationService} from '@app/core/services/internationalization.service';

export enum SORTING_TYPE {
	AZ = 0,
	NUMBER_OF_SHIFTS = 1,
	NUMBER_OF_HOURS = 2,
}

@Component({
	selector: '[app-planning-header]',
	templateUrl: './planning-header.component.html',
	styleUrls: ['./planning-header.component.scss'],
})
export class PlanningHeaderComponent implements OnInit, OnDestroy {
	public shadows = EQOShadow;

	public selectedDates$: Observable<Moment[]> = this.store.select(selectSelectedDates);
	public user$: Observable<User> = this.store.select(selectUser);
	public printing$: Observable<boolean> = this.store.select(selectPrinting);

	public dateToShiftsAmount: { [date: string]: number };
	public dateToDayNotes: { [date: string]: DayNote[] };
	public SORTING_TYPE = SORTING_TYPE;
	public filterActions: IQODropDownAction<SORTING_TYPE>[];

	public _subscriptions: Subscription[] = [];

	constructor(
		private store: Store<IAppState>,
		private interService: InternationalizationService,
	) {
		this.setSortingMode = this.setSortingMode.bind(this);
		this._subscriptions.push(
			this.selectedDates$.subscribe(async dates => {
				this.datesChanged(dates);
			}),
			this.store.select(selectDayNotes).subscribe(dayNotes => {
				this.dateToDayNotes = {};
				dayNotes.forEach(dayNote => {
					if (!this.dateToDayNotes[dayNote.start.format('YYYY-MM-DD')]) {
						this.dateToDayNotes[dayNote.start.format('YYYY-MM-DD')] = [];
					}
					this.dateToDayNotes[dayNote.start.format('YYYY-MM-DD')].push(dayNote);
				});
			}),
			this.store.select(selectShifts).subscribe(async shifts => {
				this.datesChanged(await this.selectedDates$.pipe(take(1)).toPromise());
				shifts.forEach(shift => {
					this.dateToShiftsAmount[shift.visible_date]++;
				});
			}),
		);
		this.filterActions = [
			{
				text: this.interService.translate.instant('SCHEDULE.SORT_NAMES'),
				onClick: this.setSortingMode,
				clickValue: SORTING_TYPE.AZ,
			},
			{
				text: this.interService.translate.instant('SCHEDULE.SORT_SHIFT_COUNT'),
				onClick: this.setSortingMode,
				clickValue: SORTING_TYPE.NUMBER_OF_SHIFTS,
			},
		];
		if (getCurrentState(this.store).session.user.isAdmin()) {
			this.filterActions.push({
				text: this.interService.translate.instant('SCHEDULE.SORT_HOURS_COUNT'),
				onClick: this.setSortingMode,
				clickValue: SORTING_TYPE.NUMBER_OF_HOURS,
			});
		}
	}

	public setSortingMode(value: SORTING_TYPE): void {
		this.store.dispatch(new SetSortingType(value));
	}

	public async datesChanged(dates: Moment[]): Promise<void> {
		this.dateToShiftsAmount = {};
		dates.forEach(date => {
			this.dateToShiftsAmount[date.format('YYYY-MM-DD')] = 0;
		});
	}

	public ngOnInit(): void {
	}

	public addNote(selectedDate: Moment): void {
		this.store.dispatch(new SetSelectedDayNote(null, selectedDate));
	}

	public viewNotes(date: Moment): void {
		this.store.dispatch(new SetSelectedDate(date));
	}

	public ngOnDestroy(): void {
		this._subscriptions.forEach(s => s.unsubscribe());
	}
}
